<template>
  <div
    class="fixed inset-0 z-50 flex items-stretch justify-center md:items-center"
  >
    <div
      class="absolute inset-0 z-40 hidden bg-black/50 md:block"
      @click="close"
    ></div>
    <div
      class="map-picker-shell relative z-50 w-full overflow-hidden bg-white md:h-[70vh] md:w-[720px] md:rounded-lg md:shadow-lg"
      @click.stop
    >
      <div
        v-if="loading"
        class="absolute inset-0 z-50 flex items-center justify-center bg-white/60"
        @click.stop
      >
        <div
          class="h-12 w-12 animate-spin rounded-full border-4 border-pink-500 border-t-transparent"
        />
      </div>
      <div
        class="z-10 border-b bg-white p-3"
        style="padding-top: calc(env(safe-area-inset-top) + 0.75rem)"
      >
        <div class="flex items-center justify-between">
          <div class="font-sans font-bold text-purple-950">
            {{ $t("map.pickALocation") }}
          </div>
          <div class="flex items-center gap-2">
            <button class="pr-4 text-sm text-gray-600" @click="close">
              {{ $t("map.cancel") }}
            </button>

            <button
              class="rounded bg-purple-900 px-4 py-[0.5rem] text-sm text-white"
              @click="save"
            >
              {{ $t("map.save") }}
            </button>
          </div>
        </div>
      </div>

      <div class="flex min-h-0 flex-1 flex-col">
        <div ref="mapContainer" class="map-area min-h-0 flex-1"></div>
        <!-- Radius slider -->
        <div
          class="border-t bg-white px-4 py-3"
          style="padding-bottom: calc(env(safe-area-inset-bottom) + 0.75rem)"
        >
          <div class="mb-1 flex items-center justify-between">
            <span class="text-xs font-semibold text-purple-950">{{
              $t("map.radius")
            }}</span>
            <span class="text-xs font-bold text-pink-500"
              >{{ radiusKm }} km</span
            >
          </div>
          <input
            v-model.number="radiusKm"
            type="range"
            :min="5"
            :max="500"
            :step="5"
            class="radius-slider w-full"
          />
          <div class="mt-0.5 flex justify-between text-[10px] text-gray-400">
            <span>5 km</span>
            <span>500 km</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch, nextTick } from "vue";

const props = withDefaults(
  defineProps<{
    initialLat?: number | null;
    initialLon?: number | null;
    initialPlace?: string | null;
    initialRadiusKm?: number;
  }>(),
  {
    initialLat: null,
    initialLon: null,
    initialPlace: null,
    initialRadiusKm: 100,
  },
);

type LeafletLocationEvent = {
  latlng?: {
    lat: number;
    lng: number;
  };
};

type LeafletMapLike = {
  setView: (coords: [number, number], zoom: number) => LeafletMapLike;
  invalidateSize: () => void;
  remove: () => void;
  on: (event: string, callback: (event: LeafletLocationEvent) => void) => void;
};

type LeafletMarkerLike = {
  getLatLng: () => { lat: number; lng: number };
  setLatLng: (coords: [number, number]) => void;
  on: (event: string, callback: () => void) => void;
  addTo: (map: LeafletMapLike) => LeafletMarkerLike;
};

type LeafletCircleLike = {
  setRadius: (radius: number) => void;
  setLatLng: (coords: [number, number]) => void;
  addTo: (map: LeafletMapLike) => LeafletCircleLike;
};

type LeafletModuleLike = {
  map: (element: HTMLElement) => LeafletMapLike;
  maplibreGL: (options: Record<string, unknown>) => {
    addTo: (map: LeafletMapLike) => unknown;
  };
  tileLayer: (
    url: string,
    options: Record<string, unknown>,
  ) => { addTo: (map: LeafletMapLike) => unknown };
  divIcon: (options: Record<string, unknown>) => unknown;
  marker: (
    coords: [number, number],
    options: Record<string, unknown>,
  ) => LeafletMarkerLike;
  circle: (
    coords: [number, number],
    options: Record<string, unknown>,
  ) => LeafletCircleLike;
};

type AddressLike = Record<string, string | undefined>;

const emit = defineEmits<{
  (
    e: "select",
    payload: {
      lat: number;
      lon: number;
      displayName: string;
      city?: string;
      country?: string;
      address?: AddressLike;
      radiusKm: number;
    },
  ): void;
  (e: "close"): void;
}>();

const mapContainer = ref<HTMLElement | null>(null);
const loading = ref(true);
const radiusKm = ref(props.initialRadiusKm);
let L: LeafletModuleLike | null = null;
let map: LeafletMapLike | null = null;
let marker: LeafletMarkerLike | null = null;
let radiusCircle: LeafletCircleLike | null = null;

function invalidateMapSize() {
  if (!map) return;
  requestAnimationFrame(() => {
    map!.invalidateSize();
  });
}

function loadLeaflet(): Promise<void> {
  return new Promise((resolve) => {
    const leafletWindow = window as Window & { L?: LeafletModuleLike };
    if (leafletWindow.L?.maplibreGL) {
      L = leafletWindow.L;
      resolve();
      return;
    }

    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);

    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => {
      const mapLibreCss = document.createElement("link");
      mapLibreCss.rel = "stylesheet";
      mapLibreCss.href = "https://unpkg.com/maplibre-gl@5/dist/maplibre-gl.css";
      document.head.appendChild(mapLibreCss);

      const mapLibreScript = document.createElement("script");
      mapLibreScript.src =
        "https://unpkg.com/maplibre-gl@5/dist/maplibre-gl.js";
      mapLibreScript.onload = () => {
        const bridgeScript = document.createElement("script");
        bridgeScript.src =
          "https://unpkg.com/@maplibre/maplibre-gl-leaflet/leaflet-maplibre-gl.js";
        bridgeScript.onload = () => {
          L = (window as Window & { L?: LeafletModuleLike }).L ?? null;
          resolve();
        };
        document.body.appendChild(bridgeScript);
      };
      document.body.appendChild(mapLibreScript);
    };
    document.body.appendChild(script);
  });
}

async function initMap() {
  await loadLeaflet();
  if (!mapContainer.value) {
    loading.value = false;
    return;
  }

  async function resolveStartCoords() {
    if (
      typeof props.initialLat === "number" &&
      typeof props.initialLon === "number"
    ) {
      return { lat: props.initialLat, lon: props.initialLon };
    }

    if ("geolocation" in navigator) {
      try {
        const pos = await new Promise<GeolocationPosition | null>((resolve) => {
          const timer = setTimeout(() => resolve(null), 3000);
          navigator.geolocation.getCurrentPosition(
            (p) => {
              clearTimeout(timer);
              resolve(p);
            },
            () => {
              clearTimeout(timer);
              resolve(null);
            },
            { timeout: 3000 },
          );
        });
        if (pos) return { lat: pos.coords.latitude, lon: pos.coords.longitude };
      } catch {
        // ignore and fall through to default
      }
    }

    return { lat: 51.505, lon: -0.09 };
  }

  const start = await resolveStartCoords();
  const lat = start.lat;
  const lon = start.lon;

  map = L!.map(mapContainer.value).setView([lat, lon], 13);
  L!
    .maplibreGL({
      style: "https://tiles.openfreemap.org/styles/positron",
    })
    .addTo(map);

  const pinHtml = `
    <svg width="28" height="36" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C7 0 3.5 3.5 3.5 8.5c0 6.5 8.5 17.5 8.5 17.5s8.5-11 8.5-17.5C20.5 3.5 17 0 12 0z" fill="#ec4899"/>
      <circle cx="12" cy="8.5" r="3.5" fill="white" opacity="0.9"/>
    </svg>`;

  const pinkIcon = L!.divIcon({
    className: "custom-pin",
    html: pinHtml,
    iconAnchor: [12, 28],
  });
  marker = L!
    .marker([lat, lon], { draggable: true, icon: pinkIcon })
    .addTo(map);
  radiusCircle = L!
    .circle([lat, lon], {
      radius: radiusKm.value * 1000,
      color: "#ec4899",
      fillColor: "#ec4899",
      fillOpacity: 0.08,
      weight: 1.5,
      dashArray: "6 4",
    })
    .addTo(map);

  marker.on("dragend", () => {
    const p = marker!.getLatLng();
    if (radiusCircle) radiusCircle.setLatLng([p.lat, p.lng]);
  });

  map.on("click", (event: LeafletLocationEvent) => {
    const latlng = event.latlng;
    if (!latlng) return;

    const { lat: la, lng: ln } = latlng;
    if (marker) marker.setLatLng([la, ln]);
    else
      marker = L!
        .marker([la, ln], { draggable: true, icon: pinkIcon })
        .addTo(map!);
    if (radiusCircle) radiusCircle.setLatLng([la, ln]);
  });

  invalidateMapSize();
  setTimeout(() => invalidateMapSize(), 120);
}

watch(radiusKm, (km) => {
  if (radiusCircle) radiusCircle.setRadius(km * 1000);
});

onMounted(async () => {
  await nextTick();
  try {
    await initMap();
  } finally {
    loading.value = false;
  }

  window.addEventListener("resize", invalidateMapSize);
  window.addEventListener("orientationchange", invalidateMapSize);
  window.visualViewport?.addEventListener("resize", invalidateMapSize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", invalidateMapSize);
  window.removeEventListener("orientationchange", invalidateMapSize);
  window.visualViewport?.removeEventListener("resize", invalidateMapSize);

  if (map) {
    map.remove();
    map = null;
  }
});

function close() {
  emit("close");
}

async function reverseGeocode(lat: number, lon: number) {
  try {
    const url = `/reverse-geocode?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}`;
    const res = await fetch(url, { headers: { "Accept-Language": "en" } });
    if (!res.ok) return null;
    const data = await res.json();
    return data || null;
  } catch {
    return null;
  }
}

async function save() {
  if (!marker) return close();
  try {
    const pos = marker.getLatLng();
    const lat = pos.lat;
    const lon = pos.lng;
    const data = await reverseGeocode(lat, lon);
    let displayName = "Selected location";
    let addr: AddressLike = {};
    let city = "";
    let country = "";

    if (data) {
      const payload =
        (data as { display_name?: string; address?: AddressLike }) || {};
      displayName = payload.display_name ?? displayName;
      addr = payload.address ?? {};
      city = String(
        addr.city ??
          addr.town ??
          addr.village ??
          addr.hamlet ??
          addr.county ??
          addr.state ??
          "",
      );
      country = String(addr.country ?? "");
    }

    emit("select", {
      lat,
      lon,
      displayName,
      city,
      country,
      address: addr,
      radiusKm: radiusKm.value,
    });
  } catch (error) {
    console.error("[MapPicker] save error", error);
    close();
  }
}
</script>

<style scoped>
.map-picker-shell {
  height: 100dvh;
  max-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.leaflet-container {
  height: 100%;
  width: 100%;
}

/* Radius slider styling */
.radius-slider {
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(to right, #ec4899, #a855f7);
  outline: none;
  cursor: pointer;
}

.radius-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  border: 2.5px solid #ec4899;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: transform 0.15s ease;
}

.radius-slider::-webkit-slider-thumb:hover {
  transform: scale(1.15);
}

.radius-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  border: 2.5px solid #ec4899;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
}

.map-area {
  min-height: 240px;
}

/* Make tiles grayscale and slightly desaturated to match the desired aesthetic. Targets tile images only. */
.leaflet-tile {
  filter: grayscale(100%) contrast(0.95) brightness(0.95) !important;
}

.maplibregl-canvas {
  filter: grayscale(100%) contrast(0.95) brightness(0.95) !important;
}

/* Custom pin container styles */
.custom-pin {
  display: inline-block;
  line-height: 0;
}

.custom-pin svg {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.18));
}
</style>
